import { INVOICES_TABLE_FILTER_OPTIONS } from '@anduin/api';
import {
  buildStepToWorkflowMap,
  EventType,
  FailureContext,
  generateEventTypeMapDefaults,
  StepToWorkflowMap,
  WorkflowEventEventTypeMap,
  WorkflowEventMetaDataMapWithDefaults,
} from '@anduin/shared';

/**
 * Workflow taxonomy owned by the invoices module. Each value is the `workflow` string
 * emitted on tracking events for funnel grouping in downstream analytics (Pendo / LogRocket).
 */
export enum InvoicesWorkflowType {
  INVOICES = 'INVOICES',
}

/** Steps emitted from the standalone Invoices page and the client-detail invoices tab. */
export enum InvoicesStep {
  VIEWED = 'INVOICES_VIEWED',
  STATUS_FILTER_CHANGED = 'INVOICES_STATUS_FILTER_CHANGED',
  SEARCHED = 'INVOICES_SEARCHED',
  COLUMN_FILTER_CHANGED = 'INVOICES_COLUMN_FILTER_CHANGED',
  SORT_CHANGED = 'INVOICES_SORT_CHANGED',
  ROW_SELECTION_CHANGED = 'INVOICES_ROW_SELECTION_CHANGED',
  SELECTION_CLEARED = 'INVOICES_SELECTION_CLEARED',
  BULK_DOWNLOAD_CLICKED = 'INVOICES_BULK_DOWNLOAD_CLICKED',
  PDF_VIEWED = 'INVOICES_PDF_VIEWED',
  RESEND_DIALOG_OPENED = 'INVOICES_RESEND_DIALOG_OPENED',
  RESEND_SUBMITTED = 'INVOICES_RESEND_SUBMITTED',
  RESEND_SUCCEEDED = 'INVOICES_RESEND_SUCCEEDED',
  RESEND_FAILED = 'INVOICES_RESEND_FAILED',
  RESEND_CANCELLED = 'INVOICES_RESEND_CANCELLED',
  REVERSAL_DIALOG_OPENED = 'INVOICES_REVERSAL_DIALOG_OPENED',
  REVERSAL_SUBMITTED = 'INVOICES_REVERSAL_SUBMITTED',
  REVERSAL_SUCCEEDED = 'INVOICES_REVERSAL_SUCCEEDED',
  REVERSAL_FAILED = 'INVOICES_REVERSAL_FAILED',
  REVERSAL_REJECTED = 'INVOICES_REVERSAL_REJECTED',
  REVERSAL_CANCELLED = 'INVOICES_REVERSAL_CANCELLED',
}

export type InvoicesWorkflowStep = InvoicesStep;

export type InvoicesSurface = 'STANDALONE' | 'CLIENT_DETAIL';

/** Context fields stamped onto every Invoices event by `InvoicesTrackService`. */
export interface InvoicesBaseMetadata {
  statusFilter: INVOICES_TABLE_FILTER_OPTIONS;
  surface: InvoicesSurface;
  /**
   * Comma-bracketed dimension list: leading + trailing comma, alphabetised. Examples:
   *   `` (none),
   *   `,column:client,search,statusFilter:Unpaid,`.
   */
  activeFilters: string;
}

export interface InvoicesRowSelectionChangedMetadata extends InvoicesBaseMetadata {
  selectedCount: number;
}

export interface InvoicesSelectionClearedMetadata extends InvoicesBaseMetadata {
  previousCount: number;
}

export interface InvoicesBulkDownloadClickedMetadata extends InvoicesBaseMetadata {
  selectedCount: number;
  downloadableCount: number;
}

export interface InvoicesStatusFilterChangedMetadata extends InvoicesBaseMetadata {
  from: INVOICES_TABLE_FILTER_OPTIONS | null;
  to: INVOICES_TABLE_FILTER_OPTIONS;
}

export interface InvoicesSearchedMetadata extends InvoicesBaseMetadata {
  queryLength: number;
}

export interface InvoicesColumnFilterChangedMetadata extends InvoicesBaseMetadata {
  columnId: string;
}

export interface InvoicesSortChangedMetadata extends InvoicesBaseMetadata {
  columnId: string;
  direction: 'asc' | 'desc' | null;
}

export interface InvoicesPdfViewedMetadata extends InvoicesBaseMetadata {
  invoiceId: number;
}

/** Shared shape for the Resend and Reversal workflows — both target a single invoice. */
export interface InvoicesInvoiceTargetMetadata extends InvoicesBaseMetadata {
  invoiceId: number;
  invoiceStatus: string;
}

export interface InvoicesInvoiceTargetSucceededMetadata extends InvoicesInvoiceTargetMetadata {
  operationDurationMs: number;
}

export interface InvoicesInvoiceTargetFailedMetadata extends InvoicesInvoiceTargetMetadata, FailureContext {
  operationDurationMs: number;
}

interface InvoicesStepMetaDataOverrides {
  [InvoicesStep.VIEWED]: InvoicesBaseMetadata;
  [InvoicesStep.STATUS_FILTER_CHANGED]: InvoicesStatusFilterChangedMetadata;
  [InvoicesStep.SEARCHED]: InvoicesSearchedMetadata;
  [InvoicesStep.COLUMN_FILTER_CHANGED]: InvoicesColumnFilterChangedMetadata;
  [InvoicesStep.SORT_CHANGED]: InvoicesSortChangedMetadata;
  [InvoicesStep.ROW_SELECTION_CHANGED]: InvoicesRowSelectionChangedMetadata;
  [InvoicesStep.SELECTION_CLEARED]: InvoicesSelectionClearedMetadata;
  [InvoicesStep.BULK_DOWNLOAD_CLICKED]: InvoicesBulkDownloadClickedMetadata;
  [InvoicesStep.PDF_VIEWED]: InvoicesPdfViewedMetadata;
  [InvoicesStep.RESEND_DIALOG_OPENED]: InvoicesInvoiceTargetMetadata;
  [InvoicesStep.RESEND_SUBMITTED]: InvoicesInvoiceTargetMetadata;
  [InvoicesStep.RESEND_SUCCEEDED]: InvoicesInvoiceTargetSucceededMetadata;
  [InvoicesStep.RESEND_FAILED]: InvoicesInvoiceTargetFailedMetadata;
  [InvoicesStep.RESEND_CANCELLED]: InvoicesInvoiceTargetMetadata;
  [InvoicesStep.REVERSAL_DIALOG_OPENED]: InvoicesInvoiceTargetMetadata;
  [InvoicesStep.REVERSAL_SUBMITTED]: InvoicesInvoiceTargetMetadata;
  [InvoicesStep.REVERSAL_SUCCEEDED]: InvoicesInvoiceTargetSucceededMetadata;
  [InvoicesStep.REVERSAL_FAILED]: InvoicesInvoiceTargetFailedMetadata;
  [InvoicesStep.REVERSAL_REJECTED]: InvoicesInvoiceTargetMetadata;
  [InvoicesStep.REVERSAL_CANCELLED]: InvoicesInvoiceTargetMetadata;
}

export type InvoicesWorkflowStepMetaDataMap = WorkflowEventMetaDataMapWithDefaults<
  InvoicesWorkflowStep,
  InvoicesStepMetaDataOverrides
>;

export type InvoicesStepMetadataInput<E extends InvoicesWorkflowStep> = Omit<
  InvoicesWorkflowStepMetaDataMap[E],
  keyof InvoicesBaseMetadata
>;

const INVOICES_WORKFLOW_STEPS = {
  [InvoicesWorkflowType.INVOICES]: InvoicesStep,
} as const;

export const INVOICES_STEP_TO_WORKFLOW: StepToWorkflowMap<typeof INVOICES_WORKFLOW_STEPS> =
  buildStepToWorkflowMap(INVOICES_WORKFLOW_STEPS);

export const INVOICES_STEP_TO_EVENT_TYPE: WorkflowEventEventTypeMap<InvoicesWorkflowStep> = {
  ...generateEventTypeMapDefaults(InvoicesStep, EventType.USER),
  [InvoicesStep.RESEND_SUCCEEDED]: EventType.SYSTEM,
  [InvoicesStep.RESEND_FAILED]: EventType.ERROR,
  [InvoicesStep.REVERSAL_SUCCEEDED]: EventType.SYSTEM,
  [InvoicesStep.REVERSAL_FAILED]: EventType.ERROR,
};
